import type { GrammarRule } from './grammar'

/**
 * German grammar, from A1 to C2.
 *
 * German's difficulty for an English speaker is mostly architecture, not
 * vocabulary: a verb held in second place, a partner verb or prefix pushed to
 * the end, and a clause type that sends the verb to the end instead. Once that
 * frame is visible, most of the "exceptions" turn out to be the same frame
 * again. So the A1 and A2 rules build it piece by piece — verb second, then the
 * split verbs, modals and perfect that share its shape — before cases and
 * endings, and the upper levels move from grammar to register.
 */
export const GRAMMAR_DE: GrammarRule[] = [
  // ------------------------------------------------------------------ A1
  {
    id: 'de-verb-second',
    level: 1,
    topic: 'order',
    title: 'The verb is always second',
    rule: 'In a German statement the verb is the second element — not necessarily the second word. Whatever comes first, whether the subject, a time or a place, the verb follows it, and the subject moves behind the verb if it has to.',
    pattern: ['Position 1', 'Verb', 'Subject'],
    examples: [
      { text: '[Ich](Position 1) [wohne](Verb) in Chicago.', english: 'I live in Chicago.' },
      { text: '[Heute](Position 1) [arbeite](Verb) [ich](Subject) zu Hause.', english: 'Today I’m working from home.' },
      { text: '[In Berlin](Position 1) [wohnt](Verb) [meine Schwester](Subject).', english: 'My sister lives in Berlin.' },
      { text: '[Am Wochenende](Position 1) [spielen](Verb) [wir](Subject) oft Tennis.', english: 'We often play tennis on weekends.' },
    ],
    pitfall: {
      wrong: 'Heute ich arbeite zu Hause.',
      right: 'Heute arbeite ich zu Hause.',
      why: 'English lets the subject come before the verb after “today”. German doesn’t: the verb holds second place and the subject moves behind it.',
    },
    note: 'und, aber, oder and denn don’t take up a position: Aber heute arbeite ich zu Hause.',
  },
  {
    id: 'de-questions',
    level: 1,
    topic: 'questions',
    title: 'Questions start with the verb — or with the question word',
    rule: 'A yes-or-no question starts with the verb. A question with wer, was, wo or wann starts with that word, and the verb comes straight after it. German has no “do” for questions.',
    pattern: ['Question word', 'Verb', 'Subject'],
    examples: [
      { text: '[Kommst](Verb) [du](Subject) morgen?', english: 'Are you coming tomorrow?' },
      { text: '[Wo](Question word) [wohnst](Verb) [du](Subject)?', english: 'Where do you live?' },
      { text: '[Was](Question word) [machen](Verb) [Sie](Subject) beruflich?', english: 'What do you do for a living?' },
      { text: '[Hast](Verb) [du](Subject) heute Zeit?', english: 'Do you have time today?' },
    ],
    pitfall: {
      wrong: 'Wo du wohnst?',
      right: 'Wo wohnst du?',
      why: 'After the question word comes the verb, then the subject.',
    },
    note: 'English “do” has no German counterpart in a question: “Do you speak German?” is Sprichst du Deutsch?',
  },
  {
    id: 'de-gender',
    level: 1,
    topic: 'nouns',
    title: 'Every noun has a gender: der, die or das',
    rule: 'German nouns are masculine, feminine or neuter, and the article is how you know. A noun’s gender rarely follows its meaning, so learn every noun together with its article.',
    pattern: [{ word: ['der', 'die', 'das'] }, 'Noun'],
    examples: [
      { text: '[Der](der) [Tisch](Noun) ist neu.', english: 'The table is new.' },
      { text: '[Die](die) [Lampe](Noun) ist kaputt.', english: 'The lamp is broken.' },
      { text: '[Das](das) [Buch](Noun) liegt dort.', english: 'The book is over there.' },
      { text: '[Das](das) [Mädchen](Noun) heißt Anna.', english: 'The girl’s name is Anna.' },
    ],
    note: 'Grammar beats meaning: das Mädchen, “the girl”, is neuter because every noun ending in -chen is. In the plural all three become die.',
  },
  {
    id: 'de-kein-nicht',
    level: 1,
    topic: 'negation',
    title: 'kein before a noun, nicht everywhere else',
    rule: 'Use kein where English says “not a” or “no” before a noun: kein replaces ein, or stands where there would be no article. Use nicht for verbs, adjectives, and nouns with der, die or das.',
    pattern: [{ word: ['kein', 'keine'] }, { word: 'nicht' }],
    shape: 'contrast',
    examples: [
      { text: 'Ich habe [keine](keine) Zeit.', english: 'I don’t have time.' },
      { text: 'Wir haben [kein](kein) Auto.', english: 'We don’t have a car.' },
      { text: 'Das ist [nicht](nicht) mein Handy.', english: 'That’s not my phone.' },
      { text: 'Ich komme heute [nicht](nicht).', english: 'I’m not coming today.' },
    ],
    pitfall: {
      wrong: 'Ich habe nicht Zeit.',
      right: 'Ich habe keine Zeit.',
      why: 'Zeit is a noun with no article, so it takes kein — with the ending ein would have had: keine.',
    },
  },
  {
    id: 'de-separable',
    level: 1,
    topic: 'verbs',
    title: 'Separable verbs send their prefix to the end',
    rule: 'Many verbs come in two parts — aufstehen, anrufen, ankommen. In a statement or a question, the verb takes its usual place and its prefix goes to the very end of the clause.',
    pattern: ['Verb', 'Middle', 'Prefix'],
    examples: [
      { text: 'Ich [stehe](Verb) [um sieben Uhr](Middle) [auf](Prefix).', english: 'I get up at seven.' },
      { text: '[Rufst](Verb) [du mich morgen](Middle) [an](Prefix)?', english: 'Will you call me tomorrow?' },
      { text: 'Der Zug [kommt](Verb) [um acht](Middle) [an](Prefix).', english: 'The train arrives at eight.' },
    ],
    pitfall: {
      wrong: 'Ich aufstehe um sieben.',
      right: 'Ich stehe um sieben auf.',
      why: 'The prefix stays attached only in the infinitive, aufstehen. Once the verb is conjugated, it splits.',
    },
    note: 'In a clause with weil or dass, the two parts join up again at the end: weil ich früh aufstehe.',
  },
  {
    id: 'de-du-sie',
    level: 1,
    topic: 'register',
    title: 'du for friends, Sie for everyone else',
    rule: 'German has two words for “you”. du is for friends, family, children and fellow students; Sie is for strangers, colleagues you don’t know well, and anyone you’d address by their last name. Sie is always capitalized and takes the same verb form as “they”.',
    pattern: [{ word: 'du' }, { word: 'Sie' }],
    shape: 'contrast',
    examples: [
      { text: 'Wie heiß**t** [du](du)?', english: 'What’s your name? (to a friend)' },
      { text: 'Wie heiß**en** [Sie](Sie)?', english: 'What’s your name? (to a stranger)' },
      { text: 'Kann**st** [du](du) mir helfen?', english: 'Can you help me?' },
      { text: 'Könn**en** [Sie](Sie) mir helfen?', english: 'Could you help me?' },
    ],
    note: 'When in doubt, start with Sie. Switching to du is usually offered by the older or more senior person.',
  },

  // ------------------------------------------------------------------ A2
  {
    id: 'de-modals',
    level: 2,
    topic: 'verbs',
    title: 'Modal verbs send the main verb to the end',
    rule: 'können, müssen, wollen, sollen, dürfen and möchten take second place, and the verb they go with drops to the end of the sentence, unchanged.',
    pattern: ['Modal verb', 'Middle', 'Infinitive'],
    examples: [
      { text: 'Ich [kann](Modal verb) [heute nicht](Middle) [kommen](Infinitive).', english: 'I can’t come today.' },
      {
        text: 'Wir [müssen](Modal verb) [morgen früh](Middle) [aufstehen](Infinitive).',
        english: 'We have to get up early tomorrow.',
      },
      { text: '[Möchtest](Modal verb) [du etwas](Middle) [trinken](Infinitive)?', english: 'Would you like something to drink?' },
    ],
    pitfall: {
      wrong: 'Ich kann kommen heute nicht.',
      right: 'Ich kann heute nicht kommen.',
      why: 'English keeps the two verbs together. German splits them and puts everything else in between.',
    },
    note: 'This frame — one verb second, its partner at the end — is the same shape as separable verbs and the perfect tense. Once you see it, you see it everywhere.',
  },
  {
    id: 'de-perfect',
    level: 2,
    topic: 'verbs',
    title: 'The past: haben or sein, with the participle at the end',
    rule: 'Spoken German mostly uses the perfect for the past. A form of haben or sein takes second place and the past participle goes to the end. Verbs of movement or change of state take sein.',
    pattern: ['Auxiliary', 'Middle', 'Participle'],
    examples: [
      { text: 'Ich [habe](Auxiliary) [gestern Fußball](Middle) [gespielt](Participle).', english: 'I played soccer yesterday.' },
      { text: 'Wir [sind](Auxiliary) [nach Hamburg](Middle) [gefahren](Participle).', english: 'We went to Hamburg.' },
      { text: '[Hast](Auxiliary) [du schon](Middle) [gegessen](Participle)?', english: 'Have you eaten yet?' },
    ],
    pitfall: {
      wrong: 'Ich habe nach Berlin gefahren.',
      right: 'Ich bin nach Berlin gefahren.',
      why: 'Going somewhere is a change of place, so fahren takes sein.',
    },
    note: 'sein, haben and the modal verbs usually take the simple past instead, even in conversation: ich war, ich hatte, ich musste.',
  },
  {
    id: 'de-tekamolo',
    level: 2,
    topic: 'order',
    title: 'When, why, how, where: Te-Ka-Mo-Lo',
    rule: 'In the middle of a sentence German prefers time first, then cause, then manner, then place — temporal, kausal, modal, lokal. It is a strong habit rather than a law, but following it never sounds wrong.',
    pattern: ['Time', 'Cause', 'Manner', 'Place'],
    examples: [
      {
        text: 'Ich fahre [morgen](Time) [mit dem Zug](Manner) [nach Köln](Place).',
        english: 'I’m taking the train to Cologne tomorrow.',
      },
      {
        text: 'Wir sind [gestern](Time) [wegen des Regens](Cause) [zu Hause](Place) geblieben.',
        english: 'We stayed home yesterday because of the rain.',
      },
      {
        text: 'Sie geht [jeden Tag](Time) [gern zu Fuß](Manner) [ins Büro](Place).',
        english: 'She likes walking to the office every day.',
      },
    ],
    pitfall: {
      wrong: 'Ich fahre nach Köln morgen.',
      right: 'Ich fahre morgen nach Köln.',
      why: 'English says where, then when. German says when, then where.',
    },
    note: 'Any of them can still move to position 1 for emphasis — Morgen fahre ich nach Köln — and the verb stays second.',
  },
  {
    id: 'de-two-way-prepositions',
    level: 2,
    topic: 'nouns',
    title: 'in, auf, an: where is dative, where to is accusative',
    rule: 'Nine prepositions — in, an, auf, über, unter, vor, hinter, neben and zwischen — take the dative when they say where something is, and the accusative when they say where it is going.',
    pattern: ['Where: dative', 'Where to: accusative'],
    shape: 'contrast',
    examples: [
      { text: 'Das Buch liegt [auf dem Tisch](Where: dative).', english: 'The book is on the table.' },
      { text: 'Ich lege das Buch [auf den Tisch](Where to: accusative).', english: 'I’m putting the book on the table.' },
      { text: 'Wir sitzen [im Garten](Where: dative).', english: 'We’re sitting in the garden.' },
      { text: 'Die Kinder laufen [in den Garten](Where to: accusative).', english: 'The kids are running into the garden.' },
    ],
    pitfall: {
      wrong: 'Ich lege das Buch auf dem Tisch.',
      right: 'Ich lege das Buch auf den Tisch.',
      why: 'Putting something somewhere is movement toward a place, so auf takes the accusative.',
    },
    note: 'im is short for in dem, and ins for in das.',
  },
  {
    id: 'de-dative-verbs',
    level: 2,
    topic: 'verbs',
    title: 'Some verbs take the dative: helfen, gehören, gefallen',
    rule: 'A handful of very common verbs take their object in the dative — helfen, danken, antworten, gehören, gefallen. With gefallen, the thing you like is the subject and the person who likes it is in the dative.',
    pattern: ['Verb', 'Dative'],
    examples: [
      { text: 'Ich [helfe](Verb) [dir](Dative).', english: 'I’ll help you.' },
      { text: 'Das Buch [gehört](Verb) [meinem Bruder](Dative).', english: 'The book belongs to my brother.' },
      { text: 'Der Film [gefällt](Verb) [mir](Dative).', english: 'I like the movie.' },
    ],
    pitfall: {
      wrong: 'Ich helfe dich.',
      right: 'Ich helfe dir.',
      why: 'helfen takes the dative: dir, not dich.',
    },
    note: 'gefallen works backwards from “like”: Die Schuhe gefallen mir — the shoes please me.',
  },

  // ------------------------------------------------------------------ B1
  {
    id: 'de-verb-final',
    level: 3,
    topic: 'order',
    title: 'weil, dass, wenn: the verb goes to the end',
    rule: 'After a subordinating conjunction — weil, dass, wenn, ob, obwohl — the conjugated verb moves to the very end of its clause.',
    pattern: ['Conjunction', 'Middle', 'Verb'],
    examples: [
      {
        text: 'Ich lerne Deutsch, [weil](Conjunction) [ich in Wien](Middle) [arbeite](Verb).',
        english: 'I’m learning German because I work in Vienna.',
      },
      {
        text: 'Ich glaube, [dass](Conjunction) [er heute nicht](Middle) [kommt](Verb).',
        english: 'I don’t think he’s coming today.',
      },
      { text: '[Wenn](Conjunction) [es](Middle) [regnet](Verb), bleiben wir zu Hause.', english: 'If it rains, we’ll stay home.' },
    ],
    pitfall: {
      wrong: 'Ich glaube, dass er kommt heute nicht.',
      right: 'Ich glaube, dass er heute nicht kommt.',
      why: 'dass sends the verb to the end of its clause.',
    },
    note: 'When the clause comes first it counts as position 1, so the main verb comes straight after the comma: Wenn es regnet, bleiben wir zu Hause.',
  },
  {
    id: 'de-adjective-endings',
    level: 3,
    topic: 'nouns',
    title: 'Adjective endings: the article does the work, or the adjective does',
    rule: 'An adjective before a noun takes an ending. After der, die or das the article already shows the gender, so the adjective only needs -e or -en. After ein, which can’t show masculine or neuter, the adjective shows it instead: -er, -es.',
    pattern: ['Article', 'Adjective'],
    examples: [
      { text: '[Der](Article) [neu**e**](Adjective) Kollege ist nett.', english: 'The new colleague is nice.' },
      { text: '[Ein](Article) [neu**er**](Adjective) Kollege fängt morgen an.', english: 'A new colleague starts tomorrow.' },
      { text: '[Das](Article) [klein**e**](Adjective) Café ist gemütlich.', english: 'The little café is cozy.' },
      { text: 'Wir suchen [ein](Article) [klein**es**](Adjective) Café.', english: 'We’re looking for a little café.' },
    ],
    note: 'The gender gets shown once: by the article if it can, by the adjective if it can’t.',
  },
  {
    id: 'de-konjunktiv-2',
    level: 3,
    topic: 'verbs',
    title: 'würde, hätte, wäre, könnte: polite and hypothetical',
    rule: 'The subjunctive II softens requests and describes things that aren’t real. Most verbs use würde plus the infinitive; haben, sein and the modals have forms of their own: hätte, wäre, könnte.',
    pattern: ['Subjunctive', 'Infinitive'],
    examples: [
      { text: '[Könnten](Subjunctive) Sie mir bitte [helfen](Infinitive)?', english: 'Could you help me, please?' },
      { text: 'Ich [hätte](Subjunctive) gern einen Kaffee.', english: 'I’d like a coffee.' },
      {
        text: 'Wenn ich Zeit [hätte](Subjunctive), [würde](Subjunctive) ich [mitkommen](Infinitive).',
        english: 'If I had time, I’d come along.',
      },
    ],
    note: 'Ich hätte gern… is the standard way to order in a shop or café, and far more polite than ich will.',
  },
  {
    id: 'de-passive',
    level: 3,
    topic: 'verbs',
    title: 'The passive: werden plus the participle',
    rule: 'To say what is done rather than who does it, use a form of werden with the past participle at the end. von introduces the doer, if you mention one.',
    pattern: ['Form of werden', 'Participle'],
    examples: [
      { text: 'Das Haus [wird](Form of werden) gerade [renoviert](Participle).', english: 'The house is being renovated.' },
      { text: 'Die Brücke [wurde](Form of werden) 1990 [gebaut](Participle).', english: 'The bridge was built in 1990.' },
      { text: 'Hier [wird](Form of werden) nicht [geraucht](Participle).', english: 'There’s no smoking here.' },
    ],
    note: 'Hier wird nicht geraucht has no subject at all: German can make a passive out of an activity, not just out of something acted on.',
  },
  {
    id: 'de-relative-clauses',
    level: 3,
    topic: 'nouns',
    title: 'Relative clauses: der, die, das — and the verb at the end',
    rule: 'A relative pronoun takes its gender from the noun it refers to and its case from its job in the clause. Like any subordinate clause, the verb goes to the end, and commas mark it off.',
    pattern: ['Noun', 'Relative pronoun', 'Verb'],
    examples: [
      {
        text: 'Das ist [der Mann](Noun), [der](Relative pronoun) neben mir [wohnt](Verb).',
        english: 'That’s the man who lives next door to me.',
      },
      {
        text: '[Die Frau](Noun), [mit der](Relative pronoun) ich [arbeite](Verb), kommt aus Graz.',
        english: 'The woman I work with is from Graz.',
      },
      {
        text: '[Das Buch](Noun), [das](Relative pronoun) du mir gegeben [hast](Verb), ist toll.',
        english: 'The book you gave me is great.',
      },
    ],
    pitfall: {
      wrong: 'Das ist der Mann, der wohnt neben mir.',
      right: 'Das ist der Mann, der neben mir wohnt.',
      why: 'A relative clause is a subordinate clause, so its verb goes last.',
    },
    note: 'English can leave the pronoun out — “the woman I work with”. German never can.',
  },

  // ------------------------------------------------------------------ B2
  {
    id: 'de-genitive-prepositions',
    level: 4,
    topic: 'register',
    title: 'wegen, trotz, während: the genitive in writing',
    rule: 'A few prepositions take the genitive — wegen, trotz, während and statt. Writing expects it; in conversation many people use the dative instead.',
    pattern: ['Preposition', 'Genitive'],
    examples: [
      { text: '[Trotz](Preposition) [des Regens](Genitive) sind wir spazieren gegangen.', english: 'We went for a walk despite the rain.' },
      { text: '[Während](Preposition) [des Essens](Genitive) hat niemand gesprochen.', english: 'Nobody spoke during the meal.' },
      {
        text: '[Wegen](Preposition) [eines Unfalls](Genitive) hatte der Zug Verspätung.',
        english: 'The train was late because of an accident.',
      },
    ],
    note: 'In speech you’ll hear wegen dem Regen. It is common and understood — just don’t write it.',
  },
  {
    id: 'de-modal-particles',
    level: 4,
    topic: 'particles',
    title: 'doch, mal, ja: the small words that set the tone',
    rule: 'Modal particles add no facts, only attitude. mal makes a request casual, doch adds urging or mild impatience, and ja signals “as we both know”.',
    pattern: [{ word: ['doch', 'mal', 'ja'] }],
    shape: 'contrast',
    examples: [
      { text: 'Komm [doch](doch) mit!', english: 'Oh, come along!' },
      { text: 'Kannst du [mal](mal) kurz helfen?', english: 'Could you give me a hand for a second?' },
      { text: 'Das weißt du [ja](ja) schon.', english: 'You already know that, of course.' },
      { text: 'Das ist [doch](doch) nicht so schwer.', english: 'Come on, it’s not that hard.' },
    ],
    note: 'Leave them out and nothing is wrong, but you’ll sound blunt. They are much of what makes spoken German sound natural.',
  },
  {
    id: 'de-konjunktiv-1',
    level: 4,
    topic: 'register',
    title: 'Reported speech in the news: er sagt, er habe…',
    rule: 'Newspapers and broadcasts report what someone said with the subjunctive I, which marks it as that person’s claim rather than the writer’s: er habe, sie sei. In conversation people use the ordinary indicative or würde instead.',
    pattern: ['Reporting verb', 'Subjunctive I'],
    examples: [
      {
        text: 'Der Minister [sagte](Reporting verb), er [habe](Subjunctive I) keine Zeit gehabt.',
        english: 'The minister said he hadn’t had time.',
      },
      {
        text: 'Sie [erklärte](Reporting verb), das Projekt [sei](Subjunctive I) gescheitert.',
        english: 'She explained that the project had failed.',
      },
      {
        text: 'Die Polizei [teilte mit](Reporting verb), niemand [sei](Subjunctive I) verletzt worden.',
        english: 'The police said no one had been hurt.',
      },
    ],
  },

  // ------------------------------------------------------------------ C1
  {
    id: 'de-double-infinitive',
    level: 5,
    topic: 'verbs',
    title: 'Modals in the perfect: two infinitives at the end',
    rule: 'When a modal verb goes into the perfect alongside another verb, it doesn’t use its participle. Both verbs stand at the end as infinitives, modal last: ich habe nicht kommen können.',
    pattern: ['Form of haben', 'Infinitive', 'Modal infinitive'],
    examples: [
      {
        text: 'Ich [habe](Form of haben) gestern nicht [kommen](Infinitive) [können](Modal infinitive).',
        english: 'I couldn’t come yesterday.',
      },
      {
        text: 'Wir [haben](Form of haben) lange [warten](Infinitive) [müssen](Modal infinitive).',
        english: 'We had to wait a long time.',
      },
      {
        text: '[Hast](Form of haben) du das wirklich [machen](Infinitive) [wollen](Modal infinitive)?',
        english: 'Did you really want to do that?',
      },
    ],
    pitfall: {
      wrong: 'Ich habe nicht kommen gekonnt.',
      right: 'Ich habe nicht kommen können.',
      why: 'With another verb beside it, the modal stays an infinitive. gekonnt is only for a modal on its own: Ich habe es nicht gekonnt.',
    },
    note: 'In conversation the simple past is usually easier: Ich konnte gestern nicht kommen.',
  },
  {
    id: 'de-extended-attributes',
    level: 5,
    topic: 'register',
    title: 'Long describers packed in front of the noun',
    rule: 'Formal German fits a whole relative clause in front of a noun as an extended adjective: der von allen gelobte Film, “the film praised by everyone”. Recognizing the pattern is the key to reading newspapers and reports.',
    pattern: ['Article', 'Extension', 'Participle', 'Noun'],
    examples: [
      {
        text: '[Der](Article) [von allen](Extension) [gelobte](Participle) [Film](Noun) läuft jetzt im Kino.',
        english: 'The movie everyone praised is now in theaters.',
      },
      {
        text: '[Die](Article) [seit Jahren](Extension) [steigenden](Participle) [Mieten](Noun) sind ein Problem.',
        english: 'Rents, which have been rising for years, are a problem.',
      },
      {
        text: '[Das](Article) [gestern](Extension) [eröffnete](Participle) [Museum](Noun) hat viele Besucher.',
        english: 'The museum that opened yesterday has lots of visitors.',
      },
    ],
    note: 'To read one, find the article, skip ahead to the noun, then read the words in between as “which was” or “which were”.',
  },
  {
    id: 'de-noun-verb-phrases',
    level: 5,
    topic: 'register',
    title: 'Formal German says it with a noun',
    rule: 'Official and formal German often turns a verb into a noun plus a light verb: entscheiden becomes eine Entscheidung treffen, fragen becomes eine Frage stellen. The meaning barely changes; the register does.',
    pattern: ['Noun', 'Light verb'],
    shape: 'contrast',
    examples: [
      { text: 'Wir müssen bald [eine Entscheidung](Noun) [treffen](Light verb).', english: 'We need to make a decision soon.' },
      { text: 'Darf ich [eine Frage](Noun) [stellen](Light verb)?', english: 'May I ask a question?' },
      { text: 'Das Projekt [kommt](Light verb) nicht [in Frage](Noun).', english: 'The project is out of the question.' },
      { text: 'Die Firma [stellt](Light verb) die Daten [zur Verfügung](Noun).', english: 'The company is making the data available.' },
    ],
    note: 'Each one has to be learned as a unit: you treffen a decision but stellen a question.',
  },

  // ------------------------------------------------------------------ C2
  {
    id: 'de-als-ob',
    level: 6,
    topic: 'verbs',
    title: 'als ob and als: as if',
    rule: 'To say “as if”, use als ob with the verb at the end, or als with the verb straight after it. Both take the subjunctive II.',
    pattern: [{ word: ['als ob', 'als'] }, 'Subjunctive'],
    examples: [
      { text: 'Er tut so, [als ob](als ob) er nichts davon [wüsste](Subjunctive).', english: 'He acts as if he knew nothing about it.' },
      { text: 'Er tut so, [als](als) [wüsste](Subjunctive) er nichts davon.', english: 'He’s pretending he knows nothing about it.' },
      {
        text: 'Sie sah aus, [als](als) [hätte](Subjunctive) sie tagelang nicht geschlafen.',
        english: 'She looked as if she hadn’t slept for days.',
      },
    ],
    note: 'The version with als alone is shorter and more literary; spoken German prefers als ob.',
  },
  {
    id: 'de-nominal-style',
    level: 6,
    topic: 'register',
    title: 'Nominal style: nouns where speech uses a clause',
    rule: 'Where conversation uses a clause with a verb, official and academic German often uses a noun phrase instead: nachdem Sie die Prüfung abgeschlossen haben becomes nach Abschluss der Prüfung.',
    pattern: ['Clause', 'Noun phrase'],
    shape: 'contrast',
    examples: [
      {
        text: '[Nach Abschluss der Prüfung](Noun phrase) erhalten Sie ein Zertifikat.',
        english: 'On completion of the exam, you will receive a certificate.',
      },
      {
        text: '[Nachdem Sie die Prüfung abgeschlossen haben](Clause), erhalten Sie ein Zertifikat.',
        english: 'Once you’ve finished the exam, you’ll get a certificate.',
      },
      {
        text: '[Bei Nichtbeachtung der Regeln](Noun phrase) droht ein Bußgeld.',
        english: 'Failure to observe the rules may result in a fine.',
      },
      {
        text: '[Wenn man die Regeln nicht beachtet](Clause), droht ein Bußgeld.',
        english: 'If you don’t follow the rules, you could be fined.',
      },
    ],
    note: 'Recognize it in contracts and on forms; in conversation, the clause version sounds far more natural.',
  },
]
